import React, { type FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import { Text } from '@atlaskit/primitives/compiled/text';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import EditIcon from '@atlaskit/icon/core/edit';
import FadeIn from '@atlaskit/motion/fade-in';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { ContentType as ShareContentType } from '@confluence/share-and-restrict-dialog/entry-points/ShareRestrictAndCopyCoordinator.tsx';
import { usePendingRequestExists } from '@confluence/access-requests/entry-points/usePendingRequestExists.ts';
import {
	useRequestToEditContent,
	type TriggerAnalyticsEventType,
} from '@confluence/no-permission/entry-points/useRequestToEditContent.tsx';

const i18n = defineMessages({
	askToEditContentLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit',
		description: 'The label for the button to request to edit content',
		defaultMessage: 'Ask to edit',
	},
	askToEditPendingLabel: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit.pending.label',
		description: 'The label element after the button which shows Pending request status',
		defaultMessage: 'Requested',
	},
	askToEditPendingRequestExistsFlagTitle: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit.flag.title',
		description: 'Title for the flag which confirms an existing request was sent',
		defaultMessage: 'Request pending',
	},
	askToEditPendingRequestExistsFlagDescription: {
		id: 'content-types-header.ellipsis.open.ellipsis.ask-to-edit.flag.description',
		description: 'Description for the flag which confirms an existing request was sent',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "You already sent a request. We'll send you an email when it's been approved.",
	},
});

export interface AskToEditMenuItemProps {
	contentType: ShareContentType;
	contentId: string;
	flags: FlagsStateContainer;
	triggerAnalyticsEvent: TriggerAnalyticsEventType;
	askToEditKey: string;
}

export const AskToEditMenuItem: FC<AskToEditMenuItemProps> = ({
	contentType,
	contentId,
	flags,
	triggerAnalyticsEvent,
	askToEditKey,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { requestToEditContent, hasRequestedEditPermission } = useRequestToEditContent({
		contentId,
		flags,
		triggerAnalyticsEvent,
	});

	const { pendingRequestExists, isLoadingForFirstTime: pendingRequestCheckLoading } =
		usePendingRequestExists({ contentId });

	const readyToSendRequest =
		!hasRequestedEditPermission && !pendingRequestExists && !pendingRequestCheckLoading;

	const handleItemClick = async () => {
		if (readyToSendRequest) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'requestAccessButton',
					source: 'moreActionsMenu',
					attributes: {
						contentType,
						accessType: 'edit',
						contentId,
						editDeniedButCanView: true,
					},
				},
			}).fire();
			await requestToEditContent();
		} else if (pendingRequestExists) {
			void flags.showInfoFlag({
				id: 'ask-to-edit.ellipsis.pending-edit-request-exists.flag',
				title: formatMessage(i18n.askToEditPendingRequestExistsFlagTitle),
				description: formatMessage(i18n.askToEditPendingRequestExistsFlagDescription),
				isAutoDismiss: true,
			});
		}
	};

	return (
		<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
			<DropdownItem
				key={askToEditKey}
				onClick={handleItemClick}
				elemBefore={<EditIcon label="" />}
				elemAfter={
					(hasRequestedEditPermission || pendingRequestExists) && (
						<FadeIn duration="medium">
							{(props) => (
								<div {...props}>
									<Text color="color.text.subtle">
										<FormattedMessage {...i18n.askToEditPendingLabel} />
									</Text>
								</div>
							)}
						</FadeIn>
					)
				}
			>
				<FormattedMessage {...i18n.askToEditContentLabel} />
			</DropdownItem>
		</ErrorBoundary>
	);
};
